import React, { useEffect, useMemo, useState } from "react";
import {
  formatCamelCaseToSentenceCase,
  getVal,
  useBannerContext,
} from "oolib";

import { getValidFieldsForQuantViz } from "../../InsightMatrix/utils/getValidFieldsForQuantViz";

import { DataVizWidgetShell } from "../../reports/ReportLanding/comps/WidgetRenderer/comps/DataVizWidgetShell";

import { Component as WidgetBarChart } from "../../reports/ReportLanding/comps/WidgetComps/WidgetBarChart";
import { Component as WidgetHeatMapGrid } from "../../reports/ReportLanding/comps/WidgetComps/WidgetHeatMapGrid";
import { Component as WidgetAISummary } from "../../reports/ReportLanding/comps/WidgetComps/WidgetAISummary";
import { Component as WidgetAICategorization } from "../../reports/ReportLanding/comps/WidgetComps/WidgetAICategorization";
import { Component as WidgetPieChart } from "../../reports/ReportLanding/comps/WidgetComps/WidgetPieChart";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";

import { allWidgetsBaseConfs } from "../../reports/ReportLanding/CONFIG";
import { generateWidgetActions } from "../../reports/ReportLanding/utils/generateWidgetActions";
import { getDerivedBlockTitle } from "../../reports/ReportLanding/comps/WidgetComps/WidgetBarChart/utils/_extractWidgetTitle";
import { handleVizSegmentClick } from "../utils/handleVizSegmentClick";
import { qualComps } from "../../utils/qualComps";
import { blockRegistry } from "@okf/ootils";

const qualStyle = {
  // height: `calc(100svh - 24rem)`
  height: "max-content"
}

const comps = {
  WidgetHeatMapGrid,
  WidgetBarChart,
  WidgetPieChart,
  WidgetAISummary,
  WidgetAICategorization,
};

/**
 * DataPivotVisualization
 *
 * This component is used to display a visualization of the data based on the active block.
 * If the active block is a qual component like a text editor, summary input, etc, it will render a qual widget based on the block's comp.
 * If the active block is not a qual component, it will render a bar chart based on the block's dimensions.
 * If the active block is a group question, it will render a heatmap grid based on the sub question labels.
 *
 * @param {Object} props - Props passed to the component
 * @param {Object} props.activeBlock - The active block
 * @param {Object} props.subActiveBlock - The sub active block - (breakdown)
 * @param {string} props.contentType - The content type of the data
 * @param {Array} props.activeFilters - The active filters
 */

const summarizeAfterIdx = 30 // TODO: make this dynamic based on height of viz
export const DataPivotVisualization = ({
  activeBlock,
  subActiveBlock,
  contentType,
  activeFilters,
  setActiveFilters,
  filterConfig,

}) => {

  const { SET_INFO_BANNER, SET_VARIANT } = useBannerContext()

  const platformConfigs = useGetQueryData("platformConfigs");
  const hasExperimentalTopicModelling = getAnnoMode(platformConfigs) !== "legacy";
  const isSplitMode = getAnnoMode(platformConfigs) === "split";

  const [selectedWidget, setSelectedWidget] = useState(null);

  const onVizSegmentClick = ({ value }) => {

    handleVizSegmentClick({
      clickedSegment: value,
      activeBlockFilterKey: activeBlock.filterKey,
      activeFilters,
      setActiveFilters,
      filterConfig,
      toastActions: { SET_INFO_BANNER, SET_VARIANT },
    });
  }


  useEffect(() => {
    setSelectedWidget(null)
  }, [ activeBlock.valuePath ])

  const getWidgetActions = ({ currentWidget, canAnnotate, hasBreakdown }) => {
    const actions = generateWidgetActions({
        currentComp: currentWidget,
        hasBreakdown: hasBreakdown || !!subActiveBlock,
        currentBreakdownDisplayType: selectedWidget?.breakdownDisplayType,
        canAnnotate,
        hasExperimentalTopicModelling,
        isSplitMode,
        taxonomyRole: selectedWidget?.taxonomyRole,
      })
    return actions.map((action) => ({
      ...action,
      onClick: () => {
        setSelectedWidget({
          widgetComp: action.value,
          taxonomyRole: action.taxonomyRole,
          breakdownDisplayType: action?.breakdownDisplayType,
        });
      }
    }));
  };

  // Check if it's a qual component.
  // Registered blocks use the registry (which honors instance-level qualConditions
  // like AudioInput needing transcription enabled). Unregistered blocks fall back
  // to the legacy comp-name list.
  const isQual = blockRegistry.isRegistered(activeBlock.comp)
    ? blockRegistry.isQualBlock(activeBlock)
    : qualComps.includes(activeBlock.comp);
  if (isQual) {
    // Annotation-capable via the registry (instance-aware — honours the per-block
    // kill-switch props.annotations.disable). Replaces the removed props.annotation.enable flag.
    const canAnnotate = blockRegistry.hasCapability(activeBlock.comp, 'annotation', activeBlock);

    // When the experimental new topic modelling is active and the block has
    // annotations enabled, default the qual widget to Annotations: Thematics
    // (WidgetAICategorization + taxonomyRole="thematic").
    const defaultToAnnotationsThematic = hasExperimentalTopicModelling && canAnnotate;
    const defaultWidget = defaultToAnnotationsThematic
      ? "WidgetAICategorization"
      : "WidgetAISummary";

    const currentWidget = selectedWidget?.widgetComp || defaultWidget;
    const isAnnotationsWidget = currentWidget === "WidgetAICategorization";
    // Split has no roles — its default annotations view is the flat
    // all-sub-themes list (no taxonomyRole).
    const taxonomyRole = selectedWidget?.taxonomyRole ?? (
      defaultToAnnotationsThematic && isAnnotationsWidget && !isSplitMode
        ? "thematic"
        : undefined
    );
    const QualWidget = comps[currentWidget];

    // For annotations mode, the breakdown dim must target the parent doc
    // (annotations live in their own collection). Pass { contentType, valuePath }
    // so _aggrMetricsAcrossDimensions resolves via getBlockInfo against the doc tpl.
    const breakdownDimension = subActiveBlock
      ? { contentType, valuePath: subActiveBlock.valuePath }
      : undefined;

    // Override the shell's widget label for annotation modes.
    // Experimental: "Annotation Counts: Thematics" / "Annotation Counts: Attributes".
    // Non-experimental: plain "Annotation Counts".
    const annotationLabel =
      isAnnotationsWidget
        ? taxonomyRole === "attribute"
          ? "Annotation Counts: Attributes"
          : taxonomyRole === "thematic"
          ? "Annotation Counts: Thematics"
          : "Annotation Counts"
        : undefined;
    const widgetLabel =
      annotationLabel ||
      allWidgetsBaseConfs.find(dd => dd.comp === currentWidget)?.widgetLabel ||
      undefined;

    return (
      <DataVizWidgetShell
        title={'Overview'}
        widgetLabel={widgetLabel}
        widgetActions={getWidgetActions({ currentWidget, canAnnotate })}
      >
        <QualWidget
          id={activeBlock.valuePath}
          contentType={contentType}
          blockValuePath={activeBlock.valuePath}
          label={getDerivedBlockTitle({contentType, blockValuePath: activeBlock.valuePath})}
          dimensions={[activeBlock.valuePath]}
          widgetIdx={0}
          activeFilters={activeFilters}
          summarizeAfterIdx={summarizeAfterIdx}
          style={{ ...qualStyle }}
          taxonomyRole={taxonomyRole}
          breakdownDimension={breakdownDimension}
          breakdownDisplayType={selectedWidget?.breakdownDisplayType}
          // onClick={(id) => onVizSegmentClick({ id })}
        />
      </DataVizWidgetShell>
    );
  }

  const isValidForQuantViz = getValidFieldsForQuantViz([activeBlock]).some(
    (d) => d.valuePath === activeBlock.valuePath
  );

  if (!isValidForQuantViz) return null;
  const getDimensionLabels = ({ dimensions }) => {
    if(dimensions.length < 2) return {}

    return {
      dimension1Label: getDerivedBlockTitle({contentType, blockValuePath: dimensions[0]}),
      dimension2Label: getDerivedBlockTitle({contentType, blockValuePath: dimensions[1]}),
    }
  }
  const isGroupQuestion = activeBlock.comp === "GroupQuestionsInputSingle";

  const shouldEnableOnClick = true; // All comps including NumberInput now support click-to-filter

  const dimensions = !subActiveBlock
    ? [activeBlock.valuePath]
    : isGroupQuestion
    ? activeBlock.props.subQuestionLabels.map((ss) => ({
        title: ss.display,
        dimensions: [
          activeBlock.valuePath + "." + ss.value,
          subActiveBlock.valuePath,
        ],
      }))
    : [activeBlock.valuePath, subActiveBlock.valuePath];

  if (isGroupQuestion) {
    if (!subActiveBlock) {
      const currentWidget = selectedWidget?.widgetComp || "WidgetHeatMapGrid";
      const Comp = comps[currentWidget] || WidgetHeatMapGrid;
      return (
        <DataVizWidgetShell title={'Overview'}
            widgetActions={getWidgetActions({ currentWidget, hasBreakdown: true })}
        >
          <Comp
            id={activeBlock.valuePath}
            contentType={contentType}
            dimensions={dimensions}
            widgetIdx={0}
            summarizeAfterIdx={summarizeAfterIdx}
            activeFilters={activeFilters}
            {...(shouldEnableOnClick && !subActiveBlock && { onClick: (value) => onVizSegmentClick({ value }) })}
            style={{ ...qualStyle }}
            {...{...getDimensionLabels({ dimensions })}}
          />
        </DataVizWidgetShell>
      );
    } else {
      const currentWidget = selectedWidget?.widgetComp || "WidgetBarChart";
      const Comp = comps[currentWidget] || WidgetBarChart;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {dimensions.map((dims) => (
            <DataVizWidgetShell key={dims.title} title={dims.title}
              widgetActions={getWidgetActions({ currentWidget })}
            >
              <Comp
                id={activeBlock.valuePath}
                contentType={contentType}
                dimensions={dims.dimensions}
                widgetIdx={0}
                summarizeAfterIdx={summarizeAfterIdx}
                activeFilters={activeFilters}
                {...(shouldEnableOnClick && !subActiveBlock && { onClick: (value) => onVizSegmentClick({ value }) })}
                breakdownDisplayType={selectedWidget?.breakdownDisplayType}
                style={{ ...qualStyle }}
                {...{...getDimensionLabels({ dimensions })}}
              />
            </DataVizWidgetShell>
          ))}
        </div>
      );
    }
  } else {
    const currentWidget = selectedWidget?.widgetComp || "WidgetBarChart";
    const Comp = comps[currentWidget] || WidgetBarChart;
    return (
      <DataVizWidgetShell title={'Overview'}
        widgetActions={getWidgetActions({ currentWidget })}
      >
        <Comp
          id={activeBlock.valuePath}
          contentType={contentType}
          dimensions={dimensions}
          widgetIdx={0}
          activeFilters={activeFilters}
          summarizeAfterIdx={summarizeAfterIdx}
          breakdownDisplayType={selectedWidget?.breakdownDisplayType}
          style={{ ...qualStyle }}
          {...(shouldEnableOnClick && !subActiveBlock && { onClick: (value) => onVizSegmentClick({ value }) })}
          {...{...getDimensionLabels({ dimensions })}}
        />
      </DataVizWidgetShell>
    );
  }
};
